import React, { useEffect, useState } from 'react';
import {
  fetchContactDetails,
  fetchCallHistory,
  fetchContactUpdates,
  getOppsContactId,
  discardOppsLead,
  OPPS_STAGE_CONFIG,
  OPPS_BOARD_ID,
  mondayItemUrl,
} from '../api/monday';

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  indigo:        '#534AB7',
  indigoDark:    '#3C3489',
  indigoLight:   '#EEEDFE',
  teal:          '#1D9E75',
  tealLight:     '#E1F5EE',
  tealDark:      '#085041',
  red:           '#DC2626',
  redLight:      '#FEF2F2',
  redBorder:     '#FECACA',
  amber:         '#633806',
  amberLight:    '#FAEEDA',
  bgPage:        '#f5f4f1',
  bgCard:        '#ffffff',
  bgSoft:        '#f0efe9',
  border:        '#e8e6e0',
  borderSoft:    '#f3f2ef',
  textPrimary:   '#111827',
  textSecondary: '#6b7280',
  textTertiary:  '#9ca3af',
};

// Discard (we don't want to interact with this person)
const DISCARD_REASONS = [
  'Hostile — DNC',
  'Not a fit',
  'Duplicate contact',
  'Wrong contact info',
  'Unqualified lead',
];
// Lost (opportunity lost — they went another direction)
const LOST_REASONS = [
  'Went with competitor',
  'Decided not to move',
  'Non-responsive',
  'Price / Terms',
  'Other',
];

function getCol(item, colId) {
  return item.column_values?.find((c) => c.id === colId)?.text || '';
}

function getStageConfig(stage) {
  return OPPS_STAGE_CONFIG[stage] || { label: stage || 'Unknown', bg: '#f3f4f6', color: '#6b7280' };
}

// Returns 'call', 'email', or null based on what the current stage calls for
function getRecommendedAction(stage) {
  if (!stage) return null;
  const s = stage.toLowerCase();
  if (s.includes('phone call'))                         return 'call';
  if (s.includes('email') || stage === 'Contact Added') return 'email';
  return null;
}

function getNextStepLabel(stage) {
  if (!stage) return null;
  const s = stage.toLowerCase();
  if (s.includes('phone call')) return `Up next: make a call (${stage})`;
  if (s.includes('email'))      return `Up next: send an email (${stage})`;
  if (stage === 'Contact Added') return 'Up next: send an intro email';
  return null;
}

function formatPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw || '';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function outcomeColor(outcome) {
  const o = (outcome || '').toLowerCase();
  if (o.includes('reached')) return C.teal;
  if (o.includes('email'))   return C.indigo;
  return C.textTertiary;
}

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    minHeight: '100vh', backgroundColor: C.bgPage,
    display: 'flex', flexDirection: 'column',
  },
  nav: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', backgroundColor: C.bgCard,
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, zIndex: 10,
  },
  backBtn:   { background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: C.indigo, padding: '0 4px', lineHeight: 1, fontWeight: '600' },
  navTitle:  { fontSize: '15px', fontWeight: '600', color: C.textPrimary, margin: 0, flex: 1 },
  navSource: { fontSize: '12px', color: C.textTertiary },

  body: { flex: 1, padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' },

  card: { backgroundColor: C.bgCard, borderRadius: '14px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardLabel: { fontSize: '10px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' },

  // Collapsible
  sectionHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' },
  sectionHeaderLabel: { fontSize: '10px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.07em' },
  chevron: { fontSize: '14px', color: C.textTertiary, lineHeight: 1, transition: 'transform 0.15s' },

  // Contact header block
  nameBlock: { backgroundColor: C.bgCard, borderRadius: '14px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  contactName: { fontSize: '20px', fontWeight: '700', color: C.textPrimary, margin: '0 0 2px' },
  contactSub:  { fontSize: '13px', color: C.textSecondary, margin: '0 0 10px' },
  stagePill:   { display: 'inline-block', padding: '3px 11px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', marginBottom: '8px' },

  // Info rows
  infoRow:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.borderSoft}` },
  infoRowLast: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' },
  infoLabel:   { fontSize: '12px', color: C.textSecondary },
  infoValue:   { fontSize: '13px', color: C.textPrimary, fontWeight: '500' },
  infoLink:    { fontSize: '13px', fontWeight: '600', color: C.indigo, textDecoration: 'none' },
  mondayLink:  { fontSize: '11px', fontWeight: '600', color: C.textTertiary, textDecoration: 'none', letterSpacing: '0.02em', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },

  // History
  historyEntry:  { padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}` },
  historyMeta:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' },
  historyDate:   { fontSize: '11px', color: C.textTertiary },
  historyType:   { fontSize: '12px', fontWeight: '600', color: C.textSecondary },
  outcomeDot:    { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  historyOutcome:{ fontSize: '12px', fontWeight: '600' },
  historyNotes:  { fontSize: '13px', color: C.textSecondary, lineHeight: '1.45', margin: 0 },
  noHistory:     { fontSize: '13px', color: C.textTertiary, fontStyle: 'italic' },
  updateBadge:   { fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', backgroundColor: C.indigoLight, color: C.indigoDark, letterSpacing: '0.04em', textTransform: 'uppercase' },

  // Buttons
  btnPrimary:   { flex: 1, padding: '12px 8px', backgroundColor: C.indigo, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  btnTeal:      { flex: 1, padding: '12px 8px', backgroundColor: C.teal,   color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  btnSecondary: { flex: 1, padding: '12px 8px', backgroundColor: C.bgCard, color: C.textPrimary, border: `1px solid ${C.border}`, borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  btnDanger:    { flex: 1, padding: '12px 8px', backgroundColor: C.bgCard, color: C.red, border: `1px solid ${C.redBorder}`, borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  btnAmber:     { flex: 1, padding: '12px 8px', backgroundColor: C.bgCard, color: '#92400E', border: '1px solid #FCD34D', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },

  loading: { textAlign: 'center', padding: '40px 20px', color: C.textSecondary },
  error:   { background: C.redLight, borderRadius: '10px', padding: '14px', color: '#991B1B', fontSize: '13px' },
  warning: { background: C.amberLight, border: '1px solid #FCD34D', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#92400E' },
};

// ── CollapsibleCard ────────────────────────────────────────────────────────
function CollapsibleCard({ label, defaultOpen = true, right = null, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={s.card}>
      <div
        style={{ ...s.sectionHeaderRow, paddingBottom: open ? '12px' : '0' }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={s.sectionHeaderLabel}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} onClick={(e) => e.stopPropagation()}>
          {right}
          <span style={{ ...s.chevron, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
            ▾
          </span>
        </div>
      </div>
      {open && children}
    </div>
  );
}

// ── Discard / Lost Screen ──────────────────────────────────────────────────
function DiscardScreen({ oppsItem, contactId, onConfirmed, onCancel }) {
  const [mode, setMode]           = useState(null);    // 'discard' | 'lost'
  const [reason, setReason]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState(null);

  const reasons = mode === 'discard' ? DISCARD_REASONS : LOST_REASONS;

  async function handleConfirm() {
    if (!reason || !mode) return;
    setSaving(true); setError(null);
    try {
      const stage = mode === 'lost' ? 'LOST' : 'Discarded';
      await discardOppsLead(oppsItem.id, contactId, reason, stage);
      setDone(true);
      setTimeout(() => onConfirmed(), 900);
    } catch (err) {
      setError(err.message || 'Action failed — try again.');
      setSaving(false);
    }
  }

  if (saving) {
    return (
      <div style={s.page}>
        <div style={{ ...s.loading, paddingTop: '80px' }}>
          {done ? '✅ Done. Returning to Opps…' : 'Saving…'}
        </div>
      </div>
    );
  }

  const isDNC = reason === 'Hostile — DNC';

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={{ ...s.navTitle, color: mode === 'discard' ? C.red : '#92400E' }}>
          {mode === 'discard' ? 'Discard Lead' : mode === 'lost' ? 'Mark as Lost' : 'Close Opp'}
        </p>
        <span style={s.navSource}>{(oppsItem.name || '').split('|')[0].trim()}</span>
      </div>
      <div style={s.body}>
        {error && <div style={s.error}><strong>Error:</strong> {error}</div>}

        {/* Mode picker */}
        {!mode && (
          <div style={s.card}>
            <p style={s.cardLabel}>What are you closing out?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => setMode('lost')} style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: '10px',
                border: `1.5px solid #FCD34D`, backgroundColor: C.amberLight,
                color: C.amber, fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                📁 Mark as Lost
                <div style={{ fontSize: '12px', fontWeight: '400', marginTop: '3px', color: '#92400E' }}>
                  Opportunity is gone — they went another direction or won't move forward.
                </div>
              </button>
              <button onClick={() => setMode('discard')} style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: '10px',
                border: `1.5px solid ${C.redBorder}`, backgroundColor: C.redLight,
                color: C.red, fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                🚫 Discard Lead
                <div style={{ fontSize: '12px', fontWeight: '400', marginTop: '3px', color: '#991B1B' }}>
                  We don't want to interact with this person at all.
                </div>
              </button>
            </div>
            <button style={{ ...s.btnSecondary, width: '100%', marginTop: '12px', flex: 'none' }} onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}

        {/* Reason picker */}
        {mode && (
          <>
            <div style={s.card}>
              <p style={s.cardLabel}>Reason</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {reasons.map((r) => {
                  const sel = reason === r;
                  const isRed = mode === 'discard';
                  return (
                    <button key={r} onClick={() => setReason(r)} style={{
                      textAlign: 'left', padding: '11px 14px', borderRadius: '10px',
                      border: sel
                        ? `2px solid ${isRed ? C.red : '#D97706'}`
                        : `1.5px solid ${C.border}`,
                      backgroundColor: sel
                        ? (isRed ? C.redLight : C.amberLight)
                        : C.bgCard,
                      color: sel ? (isRed ? C.red : '#92400E') : C.textPrimary,
                      fontSize: '14px', fontWeight: sel ? '700' : '500',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      {r}
                      {r === 'Hostile — DNC' && <span style={{ fontSize: '11px', marginLeft: '8px', fontWeight: '400', opacity: 0.7 }}>— flags Do Not Contact</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {reason && (
              <div style={isDNC ? { ...s.warning, background: C.redLight, borderColor: C.redBorder, color: '#991B1B' } : s.warning}>
                {isDNC
                  ? '⛔ This will mark the contact as Do Not Contact.'
                  : mode === 'lost'
                    ? 'This will move the lead to Lost status on the Opps board.'
                    : 'This will Discard the lead and remove it from the active view.'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={handleConfirm}
                disabled={!reason}
                style={{
                  width: '100%', padding: '14px', border: 'none', borderRadius: '10px',
                  fontFamily: 'inherit', fontSize: '15px', fontWeight: '700',
                  backgroundColor: reason ? (mode === 'discard' ? C.red : '#D97706') : C.bgSoft,
                  color: reason ? '#fff' : C.textTertiary,
                  cursor: reason ? 'pointer' : 'not-allowed',
                }}
              >
                {mode === 'discard' ? 'Confirm Discard' : 'Confirm Lost'}
              </button>
              <button style={{ ...s.btnSecondary, flex: 'none', width: '100%' }} onClick={() => { setMode(null); setReason(''); }}>
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function OppsCard({ oppsItem, brokerName, onBack, onLogActivity, onEmail }) {
  const [contact, setContact]     = useState(null);
  const [history, setHistory]     = useState([]);
  const [updates, setUpdates]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [contactError, setError]  = useState(null);
  const [showDiscard, setShowDiscard] = useState(false);

  const contactId    = getOppsContactId(oppsItem);
  const stage        = getCol(oppsItem, 'status');
  const source       = getCol(oppsItem, 'dropdown_mksa3qbw');
  const address      = getCol(oppsItem, 'lookup_mkszkmk0');
  const listingLink  = getCol(oppsItem, 'lookup_mkt3gex5');
  const emailContent = getCol(oppsItem, 'long_text_mkt3h67e');
  const dealDate     = getCol(oppsItem, 'date1');
  const advisorName  = getCol(oppsItem, 'person');         // Advisor (people column)
  const stageCfg     = getStageConfig(stage);

  useEffect(() => {
    // Always fetch Opps item updates (comments on the opportunity record itself).
    // Contact details + call history only load if a contact is linked.
    const promises = [fetchContactUpdates(oppsItem.id)];
    if (contactId) {
      promises.push(fetchContactDetails(contactId));
      promises.push(fetchCallHistory(contactId));
    }

    Promise.allSettled(promises).then(([u, c, h]) => {
      if (u?.status === 'fulfilled') setUpdates(u.value);
      if (c?.status === 'fulfilled') setContact(c.value);
      else if (contactId) setError('Some contact data could not be loaded.');
      if (h?.status === 'fulfilled') setHistory(h.value);
      setLoading(false);
    });
  }, []); // eslint-disable-line

  if (showDiscard) {
    return (
      <DiscardScreen
        oppsItem={oppsItem}
        contactId={contactId}
        onConfirmed={onBack}
        onCancel={() => setShowDiscard(false)}
      />
    );
  }

  const displayName = contact?.name || oppsItem.name || '—';
  // Fall back to Opps board mirror columns if the linked contact wasn't fetched
  const company     = contact?.company || getCol(oppsItem, 'lookup_mkqs58br') || '';
  const phone       = contact?.phone   || getCol(oppsItem, 'lookup_mkqq91r9') || '';
  const email       = contact?.email   || getCol(oppsItem, 'email_mkr4fh3c')  || getCol(oppsItem, 'lookup_mkqqjktp') || '';

  const SHORT_SOURCE = {
    'Direct Inquiry (Phone or Email)':     'Direct Inquiry',
    'Website Brochure Downloads / Shares': 'Brochure',
    'Crexi Brochure Download':             'Crexi Brochure',
  };
  const sourceShort = SHORT_SOURCE[source] || source || '';

  const oppsUrl = mondayItemUrl(OPPS_BOARD_ID, oppsItem.id);

  // Merge call history + Monday updates, sorted newest first
  const merged = [
    ...history.map((e) => ({ ...e, source: 'log' })),
    ...updates,
  ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  return (
    <div style={s.page}>
      {/* Nav */}
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <p style={s.navTitle}>Opportunity</p>
        {sourceShort && <span style={s.navSource}>{sourceShort}</span>}
      </div>

      <div style={s.body}>
        {contactError && <div style={{ fontSize: '11px', color: C.textTertiary, padding: '4px 0 8px', textAlign: 'center' }}>⚠️ Some contact details couldn't be loaded</div>}

        {/* Name / Stage block */}
        <div style={s.nameBlock}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ ...s.stagePill, backgroundColor: stageCfg.bg, color: stageCfg.color }}>
                {stageCfg.label}
              </div>
              <p style={s.contactName}>{displayName}</p>
              <p style={s.contactSub}>{[company, contact?.title].filter(Boolean).join(' · ') || (loading ? '…' : '—')}</p>
            </div>
            <button style={s.mondayLink} onClick={() => window.open(oppsUrl, '_blank')}>↗ Monday</button>
          </div>

          {/* Advisor + Deal Date */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
            {advisorName && (
              <div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>Advisor</div>
                <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: '500' }}>{advisorName}</div>
              </div>
            )}
            {dealDate && (
              <div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>Received</div>
                <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: '500' }}>{formatDate(dealDate)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Contact info */}
        {loading && !contactError && <div style={s.loading}>Loading contact…</div>}

        {!loading && (phone || email) && (
          <CollapsibleCard label="Contact">
            {phone && (
              <div style={s.infoRow}>
                <span style={s.infoLabel}>📞 Phone</span>
                <a href={`tel:${phone.replace(/\D/g, '')}`} style={s.infoLink}>{formatPhone(phone)}</a>
              </div>
            )}
            {email && (
              <div style={phone ? s.infoRowLast : s.infoRow}>
                <span style={s.infoLabel}>✉️ Email</span>
                <a href={`mailto:${email}`} style={s.infoLink}>{email}</a>
              </div>
            )}
          </CollapsibleCard>
        )}

        {/* Source */}
        {source && (
          <CollapsibleCard label="Lead Source">
            <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: '99px', backgroundColor: C.bgSoft, color: C.textSecondary, fontSize: '13px', fontWeight: '500' }}>
              {source}
            </span>
          </CollapsibleCard>
        )}

        {/* Property of Interest */}
        {(address || listingLink) && (
          <CollapsibleCard label="Property of Interest">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {address    && <span style={{ fontSize: '14px', color: C.textPrimary }}>📍 {address}</span>}
              {listingLink && <a href={listingLink} target="_blank" rel="noreferrer" style={{ fontSize: '14px', color: C.indigo, textDecoration: 'none' }}>🔗 View Listing</a>}
            </div>
          </CollapsibleCard>
        )}

        {/* Email template */}
        {emailContent && (
          <CollapsibleCard label="Email Template" defaultOpen={false}>
            <div style={{ backgroundColor: C.bgSoft, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: C.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {emailContent}
            </div>
          </CollapsibleCard>
        )}

        {/* Contact notes */}
        {!loading && contact?.notes && (
          <CollapsibleCard label="Contact Notes">
            <p style={{ fontSize: '13px', color: C.textSecondary, margin: 0, lineHeight: 1.6 }}>{contact.notes}</p>
          </CollapsibleCard>
        )}

        {/* Activity history */}
        {!loading && (
          <CollapsibleCard label={`Activity & Notes${merged.length > 0 ? ` (${merged.length})` : ''}`} defaultOpen={true}>
            {merged.length === 0 ? (
              <p style={s.noHistory}>No activity logged yet.</p>
            ) : (
              merged.map((entry, i) => (
                <div key={i} style={{ ...s.historyEntry, ...(i === merged.length - 1 ? { borderBottom: 'none' } : {}) }}>
                  <div style={s.historyMeta}>
                    <span style={s.historyDate}>{formatDate(entry.date)}</span>
                    {entry.source === 'update' ? (
                      <>
                        <span style={s.updateBadge}>Monday Update</span>
                        {entry.author && <span style={s.historyType}>{entry.author}</span>}
                      </>
                    ) : (
                      <>
                        <span style={s.historyType}>{entry.activityType || 'Activity'}</span>
                        {entry.outcome && (
                          <>
                            <span style={{ ...s.outcomeDot, backgroundColor: outcomeColor(entry.outcome) }} />
                            <span style={{ ...s.historyOutcome, color: outcomeColor(entry.outcome) }}>{entry.outcome}</span>
                          </>
                        )}
                        {entry.broker && (
                          <span style={{ fontSize: '11px', color: C.textTertiary, marginLeft: 'auto' }}>
                            {entry.broker.split(' ')[0]}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {(entry.notes || entry.text) && (
                    <p style={s.historyNotes}>
                      {entry.source === 'update'
                        ? (entry.notes || entry.text)
                        : (() => { const t = entry.notes || entry.text || ''; return t.length > 180 ? t.slice(0, 180) + '…' : t; })()
                      }
                    </p>
                  )}
                </div>
              ))
            )}
          </CollapsibleCard>
        )}

        {/* Actions — layout adapts based on what stage recommends */}
        {(() => {
          const rec = getRecommendedAction(stage);
          const hint = getNextStepLabel(stage);
          const emailBtn = (style) => email && (
            <button style={style} onClick={() => onEmail({
              contactId,
              contactName: displayName,
              company,
              title:        contact?.title || '',
              phone,
              email,
              notes:        contact?.notes || '',
              prospectType: source || 'New Opportunity',
              priority:     '',
              prospectItemId: oppsItem.id,
              source:       'opps',
              recentActivities: history.slice(0, 3).map((h) => ({ type: h.activityType, outcome: h.outcome, date: h.date, notes: h.notes })),
            })}>Email</button>
          );

          if (rec === 'call') {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {hint && <p style={{ fontSize: '11px', fontWeight: '700', color: C.indigo, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 2px' }}>{hint}</p>}
                <button style={{ ...s.btnPrimary, flex: 'none', width: '100%', padding: '15px' }} onClick={onLogActivity}>Log Activity</button>
                {emailBtn({ ...s.btnSecondary, flex: 'none', width: '100%' })}
              </div>
            );
          }

          if (rec === 'email' && email) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {hint && <p style={{ fontSize: '11px', fontWeight: '700', color: C.teal, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 2px' }}>{hint}</p>}
                {emailBtn({ ...s.btnTeal, flex: 'none', width: '100%', padding: '15px' })}
                <button style={{ ...s.btnSecondary, flex: 'none', width: '100%' }} onClick={onLogActivity}>Log Activity</button>
              </div>
            );
          }

          // No clear recommendation — equal weight side by side
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.btnPrimary} onClick={onLogActivity}>Log Activity</button>
              {emailBtn(s.btnTeal)}
            </div>
          );
        })()}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.btnAmber}   onClick={() => setShowDiscard(true)}>Mark Lost / Discard</button>
          <button style={s.btnSecondary} onClick={onBack}>Back to Opps</button>
        </div>
      </div>
    </div>
  );
}
